import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VehicleCard } from './VehicleCard.styles';
import useCtaURLBuilder from '../../hooks/useCtaURLBuilder';
import T1VehicleCard from './VehicleCard-T1';
import T3VehicleCard from './VehicleCard-T3';
import { CarType } from '../../types/oneGraphTypes';
import { OaddEligibleDealer } from '../../hooks/Fetch/useGetOaddDealers';
import { FrenchOaddDealers } from '../../constants/environment';
import nonAudiBrandsFullList from '../../constants/listOfNonAudiBrands.json';

interface VehicleCardProps {
  vehicle: CarType;
  isTier3page: boolean;
  marketType: string;
  isImageGalleryOn: boolean;
  mainDealer?: string;
  eligibleOaddDealers: OaddEligibleDealer[];
  eligibleDealerIds: string[];
}

const VehicleResultCard: React.FC<VehicleCardProps> = ({
  vehicle,
  isTier3page,
  marketType,
  isImageGalleryOn,
  mainDealer,
  eligibleOaddDealers,
  eligibleDealerIds,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';

  const {
    titleText,
    carPrices,
    model,
    dealer,
    subtitleText,
    cartypeText,
    salesInfo,
    vin,
    colorInfo,
    commissionNumber,
    driveText,
    gearText,
    preUse,
    modelInfo,
  } = vehicle.stockCar;
  const manufacturerSpecificItems = vehicle.stockCar?.manufacturerSpecificItems;

  const modelPrice = carPrices?.find((toCheck) => toCheck.type === 'list')?.price?.valueAsText;
  const vehicleType = cartypeText;
  const isAvailableNow = salesInfo.availableFromDateInfo.value === 'Immediately';
  const bodyType = useMemo(() => {
    const bodyInfo = manufacturerSpecificItems?.cdbItems?.find((item) => item?.id === 'body-type');
    if (bodyInfo?.value?.length) return bodyInfo?.value;
    return '';
  }, [manufacturerSpecificItems?.cdbItems]);
  const saleOrderType = salesInfo.saleOrderTypeText;
  const vehicleOrderStatus = salesInfo.orderStatusText;
  const vehicleInventoryType = cartypeText;
  const kvpsSyncId = dealer.id;
  const isUsedVehicle = cartypeText === 'U';

  const isReservedVehicle = saleOrderType?.includes('SOLD');

  const market = useMemo(() => {
    if (marketType === 'USA') return isUsedVehicle || vehicleType === 'non-audi' ? 'usuc' : 'us';

    return isUsedVehicle || vehicleType === 'non-audi' ? 'cauc' : 'ca';
  }, [isUsedVehicle, marketType, vehicleType]);

  // Make(code) - like AA (means Audi)
  const modelMake = useMemo(() => {
    const makeCode = modelInfo?.genericModel?.code?.substring(0, 2);
    if (!makeCode || makeCode.length === 0) return 'null';
    const makeCodeKey = makeCode as keyof typeof nonAudiBrandsFullList;
    const makeText = nonAudiBrandsFullList[makeCodeKey]?.text;
    return makeCode === 'AA' ? 'Audi' : makeText;
  }, [modelInfo?.genericModel?.code]);

  const vdpUrl = useCtaURLBuilder(marketType);

  const vehicleDetailsUrl = useMemo(() => {
    const query = `market=${market}&vehicleId=${vin}`;

    // Tier 3 Page Directly
    if (isTier3page) {
      return `${vdpUrl}?isdealer&${query}`;
    }

    // From here it's Tier 1 logic
    const vehicleDealerId = kvpsSyncId?.slice(3) ?? '';
    const isTier3Redirect = eligibleDealerIds.includes(vehicleDealerId);

    // Tier 1 VLP to Tier 1 VDP
    if (!isTier3Redirect) {
      return `${vdpUrl}?${query}`;
    }

    // Logic for Tier 1 VLP to Tier 3 VDP
    const tier3VdpUrl = eligibleOaddDealers.find(
      (singleDealer) => singleDealer.dealerId === vehicleDealerId,
    )?.vehicleUrl;
    // if it's hashtag return hashtag so later we can hide vdp cta
    // This logic is for the dealers that we should temporarily hide VDP CTA for them
    if (tier3VdpUrl === '#') return '#';
    const frenchTier3VdpUrl = tier3VdpUrl
      ?.replace('/en/', '/fr/')
      ?.replace('/inventory/vehicle/', '/inventaire/vehicule/');

    // If we are on a French Page and that dealership has a french VDP then from Tier 1 VLP French
    // We have to redirect to Tier 3 VDP French
    if (lang === 'fr' && FrenchOaddDealers.includes(vehicleDealerId)) {
      return `${frenchTier3VdpUrl}?isdealer&${query}`;
    }

    // otherwise we always have to redirect to Tier 3 VDP English
    return `${tier3VdpUrl}?isdealer&${query}`;
  }, [eligibleDealerIds, eligibleOaddDealers, isTier3page, kvpsSyncId, lang, market, vdpUrl, vin]);

  // SHOULD TEST LATER
  const parsedModelName = useMemo(() => {
    return titleText?.replace(/^\d{4}\s/, '');
  }, [titleText]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      vehicleOrderStatus &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(vehicleOrderStatus))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      <VehicleCard
        data-component="result-tile"
        data-testid="result-tile"
        data-vehicle-model-name={parsedModelName}
        data-vehicle-body-type={bodyType ?? ''}
        data-vehicle-model-year={model?.salesModelyear}
        data-vehicle-price={modelPrice}
        data-vehicle-dealer-name={dealer.name}
        data-vehicle-preused-text={preUse?.text}
        data-vehicle-preused-code={preUse?.code}
        data-vehicle-sale-type={
          vehicleType === 'new' && isAvailableNow
            ? 'available-now'
            : saleOrderType || vehicleInventoryType
        }
        data-vehicle-sale-order-type={
          saleOrderType === 'SOLD - Customer Sold Order' ? 'Reserved' : 'null'
        }
        data-vehicle-status={
          vehicleType === 'new' && isAvailableNow ? 'null' : vehicleOrderStatus || 'Unavailable'
        }
        data-vehicle-vin={vin}
        data-vehicle-make={modelMake}
        data-vehicle-exterior-color={colorInfo?.exteriorColor?.colorInfo?.text ?? ''}
        data-vehicle-stock-number={commissionNumber}
        data-vehicle-drivetrain={driveText}
        data-vehicle-gear={gearText}
        data-vehicle-trim={subtitleText}
        data-vehicle-dealer-code={kvpsSyncId}
        data-vehicle-inventory-type={vehicleType}
        data-cy="vehicle-card"
        // No Hover effect Anymore
        noHoverEffect
        isReservedVehicle={isReservedVehicle && isTier3page}
      >
        {isTier3page ? (
          <T3VehicleCard
            isImageGalleryOn={isImageGalleryOn}
            marketType={marketType}
            vehicle={vehicle}
            vehicleDetailsUrl={vehicleDetailsUrl}
            mainDealer={mainDealer}
          />
        ) : (
          <T1VehicleCard
            isImageGalleryOn={isImageGalleryOn}
            marketType={marketType}
            vehicle={vehicle}
            vehicleDetailsUrl={vehicleDetailsUrl}
          />
        )}
      </VehicleCard>
    </>
  );
};

export default VehicleResultCard;
